
import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getTurnstileSiteKey, isTurnstileEnabled } from '../utils/runtimeConfig';

/**
 * Cloudflare Turnstile - Invisible/Background CAPTCHA
 *
 * Runs in invisible mode - no user interaction required for most visitors.
 * Returns a token via the `onVerify` callback which must be sent to the backend
 * for server-side validation.
 *
 * Site key priority (highest to lowest):
 *   1. /config.json → turnstile.siteKey (runtime - no rebuild needed)
 *   2. VITE_TURNSTILE_SITE_KEY env var (build-time)
 *   3. Test key 1x00000000000000000000AA (always passes - safe for dev)
 *
 * Turnstile dummy site keys (for local testing):
 *   - 1x00000000000000000000AA → always passes
 *   - 2x00000000000000000000AB → always blocks
 *   - 3x00000000000000000000FF → forces interactive challenge
 */

export const useTurnstile = ({ onVerify, onExpire, onError }) => {
  
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onVerify, onExpire, onError });

  // Keep callbacks fresh without retriggering renders
  //useEffect(() => {
  //  callbacksRef.current = { onVerify, onExpire, onError };
    //}, [onVerify, onExpire, onError]);

    const initializedRef = useRef(false);

    useEffect(() => {
        if (initializedRef.current) return;   // 🔥 HARD LOCK
        initializedRef.current = true;

        let interval = null;

        const init = () => {
            if (!isTurnstileEnabled()) return;
            if (!window.turnstile) return;
            if (!containerRef.current) return;
            if (widgetIdRef.current !== null) return;

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: getTurnstileSiteKey(),
                size: "normal",

                callback: (token) => {
                    callbacksRef.current.onVerify?.(token);
                },

                "expired-callback": () => {
                    callbacksRef.current.onExpire?.();
                },

                "error-callback": (err) => {
                    callbacksRef.current.onError?.(err);
                },
            });
        };

        if (window.turnstile) {
            init();
        } else {
            interval = setInterval(() => {
                if (window.turnstile) {
                    clearInterval(interval);
                    init();
                }
            }, 100);
        }

        return () => {
            if (interval) clearInterval(interval);

            if (window.turnstile && widgetIdRef.current !== null) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch { }

                widgetIdRef.current = null;
            }

            initializedRef.current = false; // allow clean remount if needed
        };
    }, []);

    
    const render = useCallback(() => {
        
    if (!window.turnstile || !containerRef.current || widgetIdRef.current !== null) return;
    if (!isTurnstileEnabled()) return; // skip if disabled via config

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: getTurnstileSiteKey(),
   //  size: 'invisible',
     size: 'normal',
     //   execution: 'execute', 
       // appearance: 'execute',
    //  execution: 'render', // automatically run challenge on render (background mode)
        callback: (token) => {    

          //  console.log("TURNSTILE TOKEN :"+token);
           
        if (callbacksRef.current.onVerify) callbacksRef.current.onVerify(token);
      },
      'expired-callback': () => {
        if (callbacksRef.current.onExpire) callbacksRef.current.onExpire();
      },
        'error-callback': (err) => {
            console.log("Turnstile Error:", err);
        if (callbacksRef.current.onError) callbacksRef.current.onError(err);
      },
    });
  }, []);

  useEffect(() => {
    // Wait for Turnstile script to load
      if (window.turnstile)
      {          
            render();
      } else {
          
          const interval = setInterval(() => {
              
              if (window.turnstile)
              {
                  
                  clearInterval(interval);                  
                  render();                  
              }
      }, 100);
      // Cleanup if unmounted before script loads
      return () => clearInterval(interval);
    }

    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [render]);

  /** Trigger Turnstile to execute and return a new token */
  const execute = useCallback(() => {
    if (window.turnstile && widgetIdRef.current !== null) {
      try {
        window.turnstile.execute(widgetIdRef.current);
      } catch {}
    }
  }, []);

  /** Reset the widget to get a fresh token */
  const reset = useCallback(() => {
    if (window.turnstile && widgetIdRef.current !== null) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {}
    }
  }, []);

  /** Get the current token without triggering a new challenge */
  const getToken = useCallback(() => {
    if (window.turnstile && widgetIdRef.current !== null) {
      try {
        return window.turnstile.getResponse(widgetIdRef.current);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  return { containerRef, execute, reset, getToken };
};

/**
 * Invisible Turnstile widget component.
 * Renders an off-screen container that Turnstile mounts into.
 *
 * Usage:
 *   const handleVerify = (token) => console.log('Token:', token);
 *   <Turnstile onVerify={handleVerify} />
 */

/*Original start */
//export const Turnstile = ({ onVerify, onExpire, onError }) => {
    
//  const { containerRef } = useTurnstile({ onVerify, onExpire, onError });

//  return (
//    <div
//      ref={containerRef}
//      style={{
//        position: 'fixed',
//        bottom: 0,
//        right: 0,
//        zIndex: -1,
//        opacity: 0,
//        pointerEvents: 'none',
//      }}
//      aria-hidden="true"
//    />
//  );
//};
/*Original end */

export const Turnstile = forwardRef(
    ({ onVerify, onExpire, onError }, ref) => {

        const { containerRef, execute, reset, getToken } =
            useTurnstile({
                onVerify,
                onExpire,
                onError,
            });

        // expose methods to parent
        useImperativeHandle(ref, () => ({
            execute,
            reset,
            getToken,
        }));

        return (
            <div
                ref={containerRef}
                style={{
                    position: 'fixed',
                    bottom: 0,
                    right: 0,
                    zIndex: -1,
                    opacity: 0,
                    pointerEvents: 'none',
                }}
                aria-hidden="true"
            />
        );
    }
);

export default Turnstile;
